/**
 * useReferencePersons Hook
 *
 * Provides CRUD operations for reference persons (AI Avatars).
 * Used for managing reference images for character-consistent
 * AI image generation in Instagram reel scenes.
 */

import { useCallback, useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import type {
  IReferencePersonFormInput,
  IUseReferencePersonsReturn,
  IReferencePerson,
} from '@/@types/referencePersons';
import { REFERENCE_PERSON_CONSTANTS, validateImageFile } from '@/@types/referencePersons';

export function useReferencePersons(): IUseReferencePersonsReturn {
  // Loading states
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Queries
  const personsData = useQuery(api.referencePersons.getActiveReferencePersons);
  const persons: IReferencePerson[] = personsData ?? [];
  const personCount = persons.length;

  // Mutations
  const createPersonMutation = useMutation(api.referencePersons.createReferencePerson);
  const updatePersonMutation = useMutation(api.referencePersons.updateReferencePerson);
  const deletePersonMutation = useMutation(api.referencePersons.softDeleteReferencePerson);
  const setDefaultMutation = useMutation(api.referencePersons.setDefaultPerson);
  const addImageMutation = useMutation(api.referencePersons.addImageToReferencePerson);
  const removeImageMutation = useMutation(api.referencePersons.removeImageFromReferencePerson);
  const generateUploadUrlMutation = useMutation(api.referencePersons.generateUploadUrl);

  // Loading state
  const isLoading = personsData === undefined;

  // Create person
  const createPerson = useCallback(
    async (
      input: IReferencePersonFormInput
    ): Promise<{ success: boolean; personId?: Id<'referencePersons'>; error?: string }> => {
      setError(null);
      setIsCreating(true);

      try {
        const result = await createPersonMutation({
          name: input.name,
          description: input.description,
        });

        if (!result.success) {
          setError(result.error || 'Failed to create person');
        }

        return result;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create person';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setIsCreating(false);
      }
    },
    [createPersonMutation]
  );

  // Update person
  const updatePerson = useCallback(
    async (
      personId: Id<'referencePersons'>,
      input: Partial<IReferencePersonFormInput>
    ): Promise<{ success: boolean; error?: string }> => {
      setError(null);
      setIsUpdating(true);

      try {
        const result = await updatePersonMutation({
          personId,
          name: input.name,
          description: input.description,
        });

        if (!result.success) {
          setError(result.error || 'Failed to update person');
        }

        return result;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update person';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setIsUpdating(false);
      }
    },
    [updatePersonMutation]
  );

  // Delete person
  const deletePerson = useCallback(
    async (personId: Id<'referencePersons'>): Promise<{ success: boolean; error?: string }> => {
      setError(null);
      setIsDeleting(true);

      try {
        const result = await deletePersonMutation({ personId });

        if (!result.success) {
          setError(result.error || 'Failed to delete person');
        }

        return result;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete person';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setIsDeleting(false);
      }
    },
    [deletePersonMutation]
  );

  // Set person as default
  const setDefault = useCallback(
    async (personId: Id<'referencePersons'>): Promise<{ success: boolean; error?: string }> => {
      setError(null);

      try {
        const result = await setDefaultMutation({ personId });

        if (!result.success) {
          setError(result.error || 'Failed to set default person');
        }

        return result;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to set default person';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [setDefaultMutation]
  );

  // Add image to person
  const addImage = useCallback(
    async (
      personId: Id<'referencePersons'>,
      file: File
    ): Promise<{ success: boolean; storageId?: Id<'_storage'>; error?: string }> => {
      setError(null);
      setIsUploadingImage(true);

      try {
        // 1. Validate file
        const validation = validateImageFile(file);
        if (!validation.valid) {
          setError(validation.error || 'Invalid file');
          return { success: false, error: validation.error };
        }

        // 2. Get upload URL
        const uploadUrl = await generateUploadUrlMutation();

        // 3. Upload file to Convex storage
        const response = await fetch(uploadUrl, {
          method: 'POST',
          headers: { 'Content-Type': file.type },
          body: file,
        });

        if (!response.ok) {
          throw new Error('Failed to upload image');
        }

        const { storageId } = (await response.json()) as { storageId: Id<'_storage'> };

        // 4. Add image to person
        const result = await addImageMutation({
          personId,
          imageStorageId: storageId,
        });

        if (!result.success) {
          setError(result.error || 'Failed to add image');
          return { success: false, error: result.error };
        }

        return { success: true, storageId };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to add image';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setIsUploadingImage(false);
      }
    },
    [generateUploadUrlMutation, addImageMutation]
  );

  // Remove image from person
  const removeImage = useCallback(
    async (
      personId: Id<'referencePersons'>,
      storageId: Id<'_storage'>
    ): Promise<{ success: boolean; error?: string }> => {
      setError(null);

      try {
        const result = await removeImageMutation({
          personId,
          imageStorageId: storageId,
        });

        if (!result.success) {
          setError(result.error || 'Failed to remove image');
        }

        return result;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to remove image';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [removeImageMutation]
  );

  // Computed values
  const canCreatePerson = personCount < REFERENCE_PERSON_CONSTANTS.MAX_PERSONS_PER_USER;

  return {
    // Data
    persons,
    personCount,

    // Loading states
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isUploadingImage,

    // Error state
    error,

    // Actions
    createPerson,
    updatePerson,
    deletePerson,
    setDefault,
    addImage,
    removeImage,

    // Computed
    canCreatePerson,
    maxPersons: REFERENCE_PERSON_CONSTANTS.MAX_PERSONS_PER_USER,
    maxImagesPerPerson: REFERENCE_PERSON_CONSTANTS.MAX_IMAGES_PER_PERSON,
  };
}
